import {
  priceCeilingRefusalDetailsReadSchema,
  priceLockedAfterSendDetailsReadSchema,
  priceLockedWhileOpenDetailsReadSchema,
} from "@hakili/contracts";

/**
 * G-D1 (`B3-PRICING-1`): what the price door's `409` says **beside its sentence** (the contract's `vehicle-price-contract.ts`, `P0-CAT-042`).
 *
 * The client branches on the `status`, the `code` and the `details`, never on the words (4.5.5): the sentence is the server's and is drawn as served; these details are what a screen may act on
 * (the figure of the ceiling, the dossier that holds the price, the stage it stands at). Two shapes, because the door has two refusals: **ceiling**, a price above the one the dossier opened at, and
 * **locked**, a dossier open on the vehicle (the door's own open-dossier refusal, and the lock after sending, both carry `{ dossierReference, stage }`).
 */
export type PriceRefusal =
  | {
      readonly kind: "ceiling";
      readonly field: "priceAmount";
      readonly ceilingAmount: number;
      readonly dossierReference: string;
    }
  | {
      readonly kind: "locked";
      /** `LOCK-STATE`: `dossierOuvert` (open, not sent) or `envoyeBanque` (sent to a bank), from the lock's details, never from its words. */
      readonly reason: "dossierOuvert" | "envoyeBanque";
      readonly dossierReference: string;
      readonly stage: string;
    };

/** The refusal in a failure, or `undefined` for anything that is not the door's `409` with the contract's details. */
export const priceRefusalFrom = (failure: unknown): PriceRefusal | undefined => {
  const { status, code, details } = (failure ?? {}) as {
    status?: unknown;
    code?: unknown;
    details?: unknown;
  };
  if (status !== 409 || code !== "conflict") return undefined;
  const ceiling = priceCeilingRefusalDetailsReadSchema.safeParse(details);
  if (ceiling.success) {
    const { field, ceilingAmount, dossierReference } = ceiling.data;
    return { kind: "ceiling", field, ceilingAmount, dossierReference };
  }
  const sent = priceLockedAfterSendDetailsReadSchema.safeParse(details);
  if (sent.success) {
    const { reason, dossierReference, stage } = sent.data;
    return { kind: "locked", reason, dossierReference, stage };
  }
  const open = priceLockedWhileOpenDetailsReadSchema.safeParse(details);
  if (open.success) {
    const { reason, dossierReference, stage } = open.data;
    return { kind: "locked", reason, dossierReference, stage };
  }
  return undefined;
};
